/**
 * The prebuilt pieces a map is laid out from.
 *
 * A component is a rectangular zone with a height. Doorways are not authored:
 * they are generated wherever two components touch (see `compileMap`), which is
 * what makes the 2D editor a matter of putting rectangles side by side.
 */

/** Metres per grid cell. Every footprint and asset snaps to this. */
export const CELL_SIZE = 2;

export type ComponentKind = 'room' | 'hall' | 'junction' | 'bay';

export interface ComponentSpec {
  kind: ComponentKind;
  label: string;
  /** Footprint in grid cells: [along x, along z]. */
  cells: [number, number];
  /** Interior height, metres. */
  height: number;
}

export const COMPONENT_SPECS: Record<ComponentKind, ComponentSpec> = {
  room: { kind: 'room', label: 'Room', cells: [6, 4], height: 4 },
  hall: { kind: 'hall', label: 'Hallway', cells: [2, 8], height: 4 },
  junction: { kind: 'junction', label: 'Junction', cells: [4, 4], height: 5 },
  bay: { kind: 'bay', label: 'Bay', cells: [4, 3], height: 3 },
};

export const COMPONENT_KINDS: readonly ComponentKind[] = ['room', 'hall', 'junction', 'bay'];

export function componentSpec(kind: ComponentKind): ComponentSpec {
  return COMPONENT_SPECS[kind];
}

/**
 * A component's footprint in cells.
 *
 * `cells` is the footprint a placed component already stored, and it is
 * authoritative: it is the post-rotation size, so applying the rotation again
 * here would cancel the turn out. The rotation only matters when no cells are
 * given, i.e. when a component is first stamped.
 */
export function footprint(
  kind: ComponentKind,
  rotation: 0 | 90 | 180 | 270,
  cells?: [number, number],
): [number, number] {
  if (cells) return [cells[0], cells[1]];
  const base = componentSpec(kind).cells;
  return rotation === 90 || rotation === 270 ? [base[1], base[0]] : [base[0], base[1]];
}
